import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';

const STEP_LABELS: Record<number, string> = {
  1: 'Identidad Legal',
  2: 'Información de Contacto',
  3: 'Detalles del Negocio',
  4: 'Identificadores Corporativos',
  5: 'Documentos Legales',
  6: 'Beneficiarios Finales',
};

const FIELD_LABELS: Record<string, string> = {
  company_name: 'Nombre de la empresa',
  legal_rep_first_name: 'Nombre(s) del representante legal',
  legal_rep_last_name: 'Apellido(s) del representante legal',
  legal_rep_email: 'Email del representante legal',
  legal_rep_phone: 'Teléfono del representante legal',
  legal_rep_is_ubo: 'Rep. legal es beneficiario final',
  company_email: 'Email empresarial',
  company_phone: 'Teléfono empresarial',
  company_country_code: 'Código de país',
  website: 'Sitio web',
  business_activity: 'Actividad empresarial',
  address: 'Dirección',
  colonia: 'Colonia',
  city: 'Ciudad',
  state: 'Estado',
  zip_code: 'Código postal',
  country: 'País',
  incorporation_date: 'Fecha de constitución',
  nationality: 'Nacionalidad',
  rfc: 'RFC',
  cnbv_registration: 'Registro CNBV',
};

// Fields that are internal metadata — never shown to the user
const SKIP_FIELDS = new Set(['order', 'title', 'type', 'stepId', 'funnelId', 'userId']);

// Steps that are purely document/UBO steps — no text data to display
const SKIP_STEPS = new Set([5, 6, 7]);

const formatValue = (value: unknown): string => {
  if (value === true) return 'Sí';
  if (value === false) return 'No';
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'bigint') return '';
  return String(value);
};

interface StepEntry {
  order: number;
  label: string;
  fields: [string, string][];
}

const normalizeStepData = (
  stepData: Record<number, Record<string, unknown>> | unknown[]
): StepEntry[] => {
  // Array shape: [{ order, title, type, data: { field: value } }, ...]
  if (Array.isArray(stepData)) {
    return stepData
      .filter(
        (s): s is { order: number; title?: string; data?: Record<string, unknown> } =>
          typeof s === 'object' && s !== null && 'order' in s
      )
      .filter(s => !SKIP_STEPS.has(s.order))
      .map(s => ({
        order: s.order,
        label: STEP_LABELS[s.order] ?? s.title ?? `Paso ${s.order}`,
        fields: Object.entries(s.data ?? {})
          .filter(([k, v]) => !SKIP_FIELDS.has(k) && v !== null && v !== undefined && v !== '')
          .map(([k, v]) => [FIELD_LABELS[k] ?? k, formatValue(v)] as [string, string])
          .filter(([, v]) => v !== ''),
      }))
      .filter(s => s.fields.length > 0);
  }

  // Record shape: { [stepOrder]: { field: value } }
  return Object.entries(stepData)
    .map(([key, data]) => ({ order: Number(key), data }))
    .filter(({ order }) => !SKIP_STEPS.has(order))
    .map(({ order, data }) => ({
      order,
      label: STEP_LABELS[order] ?? `Paso ${order}`,
      fields: Object.entries(data)
        .filter(([k, v]) => !SKIP_FIELDS.has(k) && v !== null && v !== undefined && v !== '')
        .map(([k, v]) => [FIELD_LABELS[k] ?? k, formatValue(v)] as [string, string])
        .filter(([, v]) => v !== ''),
    }))
    .filter(s => s.fields.length > 0);
};

interface StepDataSummaryProps {
  stepData: Record<number, Record<string, unknown>> | unknown[];
}

export const StepDataSummary: React.FC<StepDataSummaryProps> = ({ stepData }) => {
  const steps = normalizeStepData(stepData);

  if (steps.length === 0) {
    return <p className="text-sm text-muted">No hay información enviada aún.</p>;
  }

  return (
    <div className="space-y-2">
      {steps.map(({ order, label, fields }) => (
        <Accordion
          key={order}
          disableGutters
          elevation={0}
          sx={{
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            '& .MuiAccordionSummary-expandIconWrapper': { color: 'var(--color-brand-white)' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <span className="font-medium text-brand-white text-sm">
              Paso {order}: {label}
            </span>
          </AccordionSummary>
          <AccordionDetails sx={{ borderTop: '1px solid var(--color-border)' }}>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {fields.map(([fieldLabel, value]) => (
                <div key={fieldLabel}>
                  <dt className="text-xs text-muted mb-0.5">{fieldLabel}</dt>
                  <dd className="text-sm text-brand-white font-medium">{value}</dd>
                </div>
              ))}
            </dl>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};
